/**
 * CTA Button Component JavaScript
 * 处理 CTA 按钮的动态状态和交互
 */

class CTAButtonComponent {
  constructor() {
    this.globalDebounceDelay = 2000;
    this.globalDebounceEnabled = true;
    this.debounceTimestamps = new WeakMap();
    this.debounceHandlers = new WeakMap();
    this.init();
  }

  init() {
    // 监听动态添加的按钮（最优先，确保能捕获后续添加的按钮）
    this.observeButtons();
    
    // 初始化现有按钮（立即执行）
    this.initButtons();
    
    // 延迟再次查找，确保捕获所有按钮（包括动态布局中的按钮）
    // 使用 requestAnimationFrame 确保在 DOM 渲染完成后执行
    requestAnimationFrame(() => {
      this.initButtons();
    });
    
    // 再延迟一次，确保捕获所有动态添加的按钮
    setTimeout(() => {
      this.initButtons();
    }, 100);
    
    // 额外延迟，确保捕获所有延迟加载的按钮（如异步渲染的按钮）
    setTimeout(() => {
      this.initButtons();
    }, 500);
  }

  observeButtons() {
    // 使用 MutationObserver 监听动态添加的按钮
    const observer = new MutationObserver((mutations) => {
      mutations.forEach((mutation) => {
        mutation.addedNodes.forEach((node) => {
          if (node.nodeType === Node.ELEMENT_NODE) {
            // 检查新添加的元素是否是 CTA 按钮
            if (node.classList && node.classList.contains('component-cta-button')) {
              this.initButton(node);
            }
            
            // 检查新添加的元素内部是否有 CTA 按钮
            const ctaButtons = node.querySelectorAll && node.querySelectorAll('.component-cta-button');
            if (ctaButtons) {
              ctaButtons.forEach(button => this.initButton(button));
            }
          }
        });
      });
    });

    observer.observe(document.body, {
      childList: true,
      subtree: true
    });
  }

  initButtons() {
    const buttons = document.querySelectorAll('.component-cta-button');
    buttons.forEach(button => {
      // 只初始化尚未初始化的按钮
      if (!button.dataset.initialized) {
        this.initButton(button);
      }
    });
  }

  initButton(button) {
    // 确保按钮是有效的 DOM 元素
    if (!button || !button.nodeType || button.nodeType !== Node.ELEMENT_NODE) {
      return;
    }
    
    // 确保按钮在 DOM 中（可能被移除但引用还在）
    if (!document.body.contains(button) && !document.documentElement.contains(button)) {
      return;
    }
    
    if (button.dataset.initialized) {
      return;
    }
    
    // 标记为已初始化
    button.dataset.initialized = 'true';

    // 默认启用全局防抖（支持通过 data 属性覆盖）
    // 必须在绑定其他事件之前执行，确保防抖能在最早期拦截
    this.applyGlobalDebounce(button);

    // 绑定事件监听器（在防抖之后）
    this.bindEvents(button);
    
    // 初始化按钮状态
    this.updateButtonState(button);
    
    // 检查是否需要错误检测功能
    if (button.dataset.detectError === 'true') {
      this.initErrorDetection(button);
    }
    
    // 检查是否需要贴底功能
    if (button.dataset.stickToBottom === 'true') {
      this.initStickToBottom(button);
    }
    
    // 添加加载状态方法到按钮实例
    button.setLoadingState = (isLoading) => this.setLoadingState(button, isLoading);
    // 添加软禁用方法到按钮实例（仅切换文本与可用性，不改变样式）
    button.setEnabled = (isEnabled, newText = null) => this.setEnabled(button, isEnabled, newText);
    // 添加软禁用方法到按钮实例（仅设置不可用，不改变样式）
    button.setSoftDisabled = (isDisabled, newText = null) => this.setSoftDisabled(button, isDisabled, newText);
  }

  // 初始化错误检测功能
  initErrorDetection(button) {
    if (button.dataset.errorDetectionInitialized) return;
    button.dataset.errorDetectionInitialized = 'true';

    // 查找检测范围（页面容器或 body）
    const scope = this.findPageContainer(button) || document.body;

    // 错误检查函数
    const checkErrors = () => {
      // 查找所有含有 -error 的元素
      // 使用更具体的选择器可以提高性能，但这里遵循"包含 -error"的宽泛要求
      const errorElements = scope.querySelectorAll('[class*="-error"]');
      let hasError = false;

      for (let i = 0; i < errorElements.length; i++) {
        const el = errorElements[i];
        
        // 排除按钮自身及其子元素
        if (el === button || button.contains(el)) continue;

        // 检查是否真的包含 -error 类名（避免仅仅是部分匹配）
        // 同时排除 component-error-icon 这种只是图标容器的类（如果需要的话，但这里暂不排除，除非它导致误判）
        // 关键：只检测可见的错误元素
        if (el.offsetParent !== null) {
          hasError = true;
          break;
        }
      }

      // 更新按钮状态
      if (hasError) {
        // 如果发现错误，且按钮未被禁用，强制禁用
        if (!button.disabled || !button.classList.contains('component-cta-button--disabled-by-error')) {
          button.disabled = true;
          button.classList.add('component-cta-button--disabled-by-error');
          button.classList.add('component-cta-button--disabled');
          button.classList.remove('component-cta-button--enabled');
        }
      } else {
        // 如果没有错误，且按钮之前是因为错误被禁用的，则恢复
        if (button.classList.contains('component-cta-button--disabled-by-error')) {
          button.classList.remove('component-cta-button--disabled-by-error');
          // 恢复启用状态（假设其他逻辑会正确管理状态，或者我们默认恢复为启用）
          // 注意：这可能会覆盖其他逻辑的禁用状态，但在表单验证场景中，通常修正错误后就应该启用
          button.disabled = false;
          button.classList.remove('component-cta-button--disabled');
          button.classList.add('component-cta-button--enabled');
        }
      }
    };

    // 初始检查
    // 使用 requestAnimationFrame 确保在 DOM 更新后执行
    requestAnimationFrame(checkErrors);

    // 监听页面变化（错误元素的添加/移除/显示/隐藏）
    const pageObserver = new MutationObserver((mutations) => {
      let shouldCheck = false;
      for (const m of mutations) {
        // 监听类名变化（可能添加/移除错误类，或改变 display 属性对应的类）
        if (m.type === 'attributes' && (m.attributeName === 'class' || m.attributeName === 'style')) {
          shouldCheck = true;
          break;
        }
        // 监听节点添加/移除
        if (m.type === 'childList') {
          shouldCheck = true;
          break;
        }
      }
      if (shouldCheck) {
        // 使用微任务或 rAF 避免频繁执行
        requestAnimationFrame(checkErrors);
      }
    });

    pageObserver.observe(scope, {
      attributes: true,
      attributeFilter: ['class', 'style'],
      childList: true,
      subtree: true
    });

    // 监听按钮自身变化（防止其他逻辑意外启用按钮）
    const buttonObserver = new MutationObserver((mutations) => {
      for (const m of mutations) {
        if (m.type === 'attributes' && m.attributeName === 'disabled') {
          // 如果按钮被启用，但我们仍处于错误锁定状态，强制再次检查并可能禁用
          if (!button.disabled && button.classList.contains('component-cta-button--disabled-by-error')) {
            checkErrors();
          }
        }
      }
    });

    buttonObserver.observe(button, {
      attributes: true,
      attributeFilter: ['disabled']
    });
    
    // 保存引用以便清理（虽然 CTA 按钮通常生命周期伴随页面）
    button._errorObservers = { pageObserver, buttonObserver };
  }

  applyGlobalDebounce(button) {
    // data-click-debounce="false" 可关闭；data-click-debounce-delay 覆盖默认 100ms
    const debounceAttr = button.getAttribute('data-click-debounce');
    // 只有明确设置为字符串 "false" 时才禁用防抖
    // null、空字符串、或其他值都启用防抖
    const shouldEnable = debounceAttr !== 'false' && this.globalDebounceEnabled;
    
    if (!shouldEnable) {
      return;
    }
    
    // 检查是否已经绑定过防抖处理器，避免重复绑定
    if (this.debounceHandlers.has(button)) {
      return;
    }

    // 获取防抖延迟时间
    const getDebounceDelay = () => {
      const customDelayAttr = button.getAttribute('data-click-debounce-delay');
      // 如果属性不存在或为空字符串，使用默认值
      if (customDelayAttr === null || customDelayAttr === '') {
        return this.globalDebounceDelay;
      }
      
      // 将字符串转换为数字
      const customDelay = Number(customDelayAttr);
      
      // 检查是否为有效数字且 >= 0
      // 注意：Number.isNaN 和 isFinite 确保是有效数字
      // 0 是有效的延迟值（表示立即允许下一次点击）
      if (Number.isNaN(customDelay) || !Number.isFinite(customDelay) || customDelay < 0) {
        return this.globalDebounceDelay;
      }
      
      return customDelay;
    };
    
    // 点击事件处理器（只监听 click 事件）
    const clickHandler = (event) => {
      // 如果按钮已禁用，不进行防抖检查，直接放行（让原生disabled处理）
      if (button.disabled) {
        return;
      }

      const delay = getDebounceDelay();
      const now = performance.now();
      const last = this.debounceTimestamps.get(button) || 0;
      const timeSinceLastClick = last === 0 ? 0 : now - last;

      // 如果距离上次点击时间太短，拦截事件
      if (timeSinceLastClick < delay && last !== 0) { // last !== 0 确保第一次点击不会被拦截
        event.preventDefault();
        event.stopImmediatePropagation();
        event.stopPropagation();
        return false;
      }

      // 防抖检查通过，更新时间戳
      this.debounceTimestamps.set(button, now);
    };
    
    // 在捕获阶段最早绑定，确保在其他处理器之前执行
    // 使用 { capture: true, passive: false } 确保可以 preventDefault
    button.addEventListener('click', clickHandler, { capture: true, passive: false });
    
    // 保存处理器引用，用于后续可能的清理
    this.debounceHandlers.set(button, { 
      click: clickHandler
    });
  }

  setGlobalDebounce(options = {}) {
    const { enabled, delay } = options;

    if (typeof enabled === 'boolean') {
      this.globalDebounceEnabled = enabled;
    }

    if (typeof delay === 'number' && delay >= 0) {
      this.globalDebounceDelay = delay;
    }
  }

  bindEvents(button) {
    // 鼠标事件
    button.addEventListener('mousedown', (e) => this.handleMouseDown(e, button));
    button.addEventListener('mouseup', (e) => this.handleMouseUp(e, button));
    button.addEventListener('mouseleave', (e) => this.handleMouseLeave(e, button));
    
    // 触摸事件
    button.addEventListener('touchstart', (e) => this.handleTouchStart(e, button), { passive: true });
    button.addEventListener('touchend', (e) => this.handleTouchEnd(e, button), { passive: true });
    
    // 键盘事件
    button.addEventListener('keydown', (e) => this.handleKeyDown(e, button));
    button.addEventListener('keyup', (e) => this.handleKeyUp(e, button));
  }

  handleMouseDown(e, button) {
    if (button.disabled) return;
    
    // 添加 pressed 状态
    button.classList.add('component-cta-button--pressed');
    button.classList.remove('component-cta-button--enabled');
  }

  handleMouseUp(e, button) {
    if (button.disabled) return;
    
    // 移除 pressed 状态，恢复 enabled 状态
    button.classList.remove('component-cta-button--pressed');
    button.classList.add('component-cta-button--enabled');
  }

  handleMouseLeave(e, button) {
    if (button.disabled) return;
    
    // 鼠标离开时移除 pressed 状态
    button.classList.remove('component-cta-button--pressed');
    button.classList.add('component-cta-button--enabled');
  }

  handleTouchStart(e, button) {
    if (button.disabled) return;
    
    // 添加 pressed 状态
    button.classList.add('component-cta-button--pressed');
    button.classList.remove('component-cta-button--enabled');
  }

  handleTouchEnd(e, button) {
    if (button.disabled) return;
    
    // 移除 pressed 状态，恢复 enabled 状态
    button.classList.remove('component-cta-button--pressed');
    button.classList.add('component-cta-button--enabled');
  }

  handleKeyDown(e, button) {
    if (button.disabled) return;
    
    // 空格键或回车键按下时添加 pressed 状态
    if (e.key === ' ' || e.key === 'Enter') {
      e.preventDefault();
      button.classList.add('component-cta-button--pressed');
      button.classList.remove('component-cta-button--enabled');
    }
  }

  handleKeyUp(e, button) {
    if (button.disabled) return;
    
    // 空格键或回车键释放时移除 pressed 状态
    if (e.key === ' ' || e.key === 'Enter') {
      button.classList.remove('component-cta-button--pressed');
      button.classList.add('component-cta-button--enabled');
    }
  }

  updateButtonState(button) {
    // 根据按钮的初始类名设置状态
    if (button.classList.contains('component-cta-button--pressed')) {
      // 如果按钮初始就是 pressed 状态，保持该状态
      button.classList.remove('component-cta-button--enabled');
    } else if (!button.classList.contains('component-cta-button--disabled')) {
      // 如果不是 disabled 状态，设置为 enabled
      button.classList.add('component-cta-button--enabled');
    }
  }
  
  setLoadingState(button, isLoading, loadingText = null) {
    const label = button.querySelector('.component-cta-button__label');
    
    if (isLoading) {
      // 进入加载状态
      button.classList.add('component-cta-button--loading');
      button.disabled = true;
      
      // 保存原始文本并设置加载文本
      if (label) {
        button.dataset.originalText = label.textContent;
        const fallbackLoadingText = window.getFrontendMessage
          ? window.getFrontendMessage('submitting', 'Submitting')
          : 'Submitting';
        const textToShow = loadingText || button.dataset.loadingText || fallbackLoadingText;
        label.textContent = textToShow;
      }
    } else {
      // 退出加载状态
      button.classList.remove('component-cta-button--loading');
      button.disabled = false;
      
      // 恢复原始文本
      if (label && button.dataset.originalText) {
        label.textContent = button.dataset.originalText;
        delete button.dataset.originalText;
      }
    }
    
    this.updateButtonState(button);
  }

  // 公共方法：设置按钮状态
  setButtonState(button, state) {
    // 移除所有状态类
    button.classList.remove('component-cta-button--enabled', 'component-cta-button--pressed', 'component-cta-button--disabled');
    
    // 添加新的状态类
    if (state === 'enabled') {
      button.classList.add('component-cta-button--enabled');
      button.disabled = false;
    } else if (state === 'pressed') {
      button.classList.add('component-cta-button--pressed');
      button.disabled = false;
    } else if (state === 'disabled') {
      // 硬禁用：设置不可用并添加禁用样式类
      button.classList.add('component-cta-button--disabled');
      button.disabled = true;
    }
  }

  // 公共方法：设置按钮样式
  setButtonStyle(button, style) {
    // 移除所有样式类
    button.classList.remove('component-cta-button--primary', 'component-cta-button--secondary', 'component-cta-button--tertiary');
    
    // 添加新的样式类
    if (['primary', 'secondary', 'tertiary'].includes(style)) {
      button.classList.add(`component-cta-button--${style}`);
    }
  }

  // 软禁用：仅切换可用性与文本，不改变样式类
  setEnabled(button, isEnabled, newText = null) {
    const label = button.querySelector('.component-cta-button__label');

    button.disabled = !isEnabled;

    // 保持样式稳定：仅在启用时确保有 enabled 类，禁用时不添加 --disabled 类
    if (isEnabled) {
      button.classList.add('component-cta-button--enabled');
      button.classList.remove('component-cta-button--pressed');
    }

    if (newText && label) {
      label.textContent = newText;
    }
  }

  // 软禁用：仅设置不可用，不改变样式
  setSoftDisabled(button, isDisabled, newText = null) {
    const label = button.querySelector('.component-cta-button__label');

    button.disabled = isDisabled;

    // 不添加 --disabled 样式类，保持原有样式
    if (!isDisabled) {
      button.classList.add('component-cta-button--enabled');
      button.classList.remove('component-cta-button--pressed');
      // 确保移除 disabled 类，以防之前存在
      button.classList.remove('component-cta-button--disabled');
    }

    if (newText && label) {
      label.textContent = newText;
    }
  }

  // 初始化贴底功能
  initStickToBottom(button) {
    // 添加贴底样式类
    button.classList.add('component-cta-button--stick-to-bottom');
    
    // 依赖 CSS 控制布局，避免过多内联覆盖，仅确保基础可见性
    button.style.zIndex = '1000';
    
    // 初始化键盘检测（只初始化一次）
    if (!this.keyboardDetectionInitialized) {
      this.initKeyboardDetection();
      this.keyboardDetectionInitialized = true;
    }
  }

  // 初始化键盘检测 - 基于 focus/blur 事件（参考 password 页面实现，iOS优化）
  initKeyboardDetection() {
    const isIOS = /iPad|iPhone|iPod/.test(navigator.userAgent);
    
    // 检查输入框是否应该触发键盘
    const shouldTriggerKeyboard = (element) => {
      if (!element) return false;
      
      // 检查是否是输入框类型
      const isInput = element.tagName === 'INPUT' || 
                     element.tagName === 'TEXTAREA' ||
                     element.isContentEditable;
      
      if (!isInput) return false;
      
      // 过滤掉不应该触发键盘的输入框
      if (element.tagName === 'INPUT') {
        const inputType = element.type?.toLowerCase();
        // 隐藏类型不触发键盘
        if (inputType === 'hidden' || inputType === 'submit' || inputType === 'button' || inputType === 'reset') {
          return false;
        }
        // disabled 或 readonly 不触发键盘
        if (element.disabled || element.readOnly) {
          return false;
        }
      }
      
      // contentEditable 元素检查
      if (element.isContentEditable && element.contentEditable === 'false') {
        return false;
      }
      
      return true;
    };

    // 检查是否有任何输入框聚焦
    const isAnyInputFocused = () => {
      const activeElement = document.activeElement;
      return shouldTriggerKeyboard(activeElement);
    };

    // 设置键盘激活状态
    const setKeyboardActive = (active) => {
      this.updateKeyboardState(active);
    };

    // iOS 额外兜底：持续监听 visualViewport 变化（仅在移动端且有输入框聚焦时）
    let viewportCheckInterval = null;
    
    const startViewportCheck = () => {
      if (viewportCheckInterval || !isIOS || !window.visualViewport) return;
      
      viewportCheckInterval = setInterval(() => {
        if (window.innerWidth <= 768 && isAnyInputFocused()) {
          const viewport = window.visualViewport;
          const bottomObstruction = Math.max(0, (window.innerHeight - (viewport.height + viewport.offsetTop)));
          // 如果检测到键盘遮挡，确保按钮弹起
          if (bottomObstruction > 50) {
            setKeyboardActive(true);
          }
        } else {
          // 没有输入框聚焦或不在移动端，停止检查
          stopViewportCheck();
        }
      }, 100);
    };
    
    const stopViewportCheck = () => {
      if (viewportCheckInterval) {
        clearInterval(viewportCheckInterval);
        viewportCheckInterval = null;
      }
    };

    // iOS 上使用更长的延迟，并添加兜底检查
    const handleFocus = (e) => {
      // 只在移动端处理
      if (window.innerWidth <= 768 && shouldTriggerKeyboard(e.target)) {
        // iOS 需要更长的延迟，等待键盘完全弹出
        const delay = isIOS ? 150 : 50;
        
        // 立即设置一次（快速响应）
        setKeyboardActive(true);
        
        // 延迟再次确认（确保键盘已弹出）
        setTimeout(() => {
          if (isAnyInputFocused()) {
            setKeyboardActive(true);
          }
        }, delay);
        
        // iOS 额外检查：如果 visualViewport 可用，也监听其变化作为兜底
        if (isIOS && window.visualViewport) {
          const checkViewport = () => {
            const viewport = window.visualViewport;
            const bottomObstruction = Math.max(0, (window.innerHeight - (viewport.height + viewport.offsetTop)));
            if (bottomObstruction > 50 && isAnyInputFocused()) {
              setKeyboardActive(true);
            }
          };
          
          // 延迟检查 visualViewport（键盘弹出需要时间）
          setTimeout(() => {
            checkViewport();
            // 再检查一次，确保捕获到
            setTimeout(checkViewport, 100);
          }, 200);
          
          // 启动持续检查
          setTimeout(startViewportCheck, 200);
        }
      }
    };

    // 处理输入框失焦事件
    const handleBlur = (e) => {
      // 只在移动端处理
      if (window.innerWidth <= 768) {
        // 延迟检查，允许切换焦点到另一输入框时不抖动
        setTimeout(() => {
          if (!isAnyInputFocused()) {
            setKeyboardActive(false);
            // 停止 visualViewport 检查
            stopViewportCheck();
          }
        }, 120);
      }
    };

    // 监听所有输入框的 focus 和 blur 事件
    // 使用事件委托，支持动态添加的输入框
    document.addEventListener('focusin', (e) => {
      if (shouldTriggerKeyboard(e.target)) {
        handleFocus(e);
      }
    });

    document.addEventListener('focusout', (e) => {
      if (shouldTriggerKeyboard(e.target)) {
        handleBlur(e);
      }
    });

    // 监听窗口大小变化，在切换到移动端时重新检查
    let resizeTimeout;
    window.addEventListener('resize', () => {
      clearTimeout(resizeTimeout);
      resizeTimeout = setTimeout(() => {
        if (window.innerWidth <= 768) {
          // 移动端：检查当前是否有输入框聚焦
          setKeyboardActive(isAnyInputFocused());
        } else {
          // 桌面端：移除键盘激活状态
          setKeyboardActive(false);
        }
      }, 150);
    });

  }


  // 更新键盘状态
  updateKeyboardState(isKeyboardActive) {
    const stickButtons = document.querySelectorAll('.component-cta-button--stick-to-bottom');
    
    stickButtons.forEach(button => {
      const shouldIgnoreKeyboardAdjust = button.classList.contains('take-drive-form__supplemental-scroll');
      if (shouldIgnoreKeyboardAdjust) {
        button.classList.toggle('keyboard-active', !!isKeyboardActive);
        button.style.removeProperty('transform');
        button.style.removeProperty('transition');
        button.style.removeProperty('z-index');
        button.style.removeProperty('position');
        button.style.removeProperty('bottom');
        return;
      }

      const isIOS = /iPad|iPhone|iPod/.test(navigator.userAgent);

      if (isKeyboardActive) {
        button.classList.add('keyboard-active');

        if (isIOS) {
          // iOS：使用 transform 上推，确保按钮完全可见
          const viewport = window.visualViewport;
          const bottomObstruction = viewport
            ? Math.max(0, (window.innerHeight - (viewport.height + viewport.offsetTop)))
            : 0;

          // 按钮当前距离底部的距离
          const currentBottom = Number(button.getAttribute('data-base-bottom') || 24);
          // 距离软键盘的高度（期望的按钮与键盘顶部的间距）
          const desiredGap = 12;
          
          // 按照正确公式计算：键盘遮挡的高度 感觉其实应该加上间距,但实际看起来不用
          const push = Math.max(0, bottomObstruction);

          if (push > 0) {
            button.style.transform = `translateY(-${push}px)`;
            button.style.transition = 'transform 0.2s ease-out';
            button.style.zIndex = '10000';
          } else {
            button.style.transform = '';
            button.style.transition = '';
            button.style.zIndex = '';
          }
        } 
        else {
          // 非 iOS：智能处理，如果按钮会遮挡内容则推整个页面
          // const shouldPushPage = this.shouldPushPageInsteadOfButton(button);
          
          // if (shouldPushPage) {
          //   // 推整个页面内容
          //   this.pushPageContent(button, true);
          //   // 按钮保持贴底，与键盘保持12px间距
          //   button.style.position = 'fixed';
          //   button.style.bottom = '12px';
          //   button.style.zIndex = '1000';
          //   button.style.transform = '';
          //   button.style.transition = '';
          // } else {
            // 只弹起按钮，与键盘保持12px间距
            button.style.position = 'fixed';
            button.style.bottom = '12px';
            button.style.zIndex = '1000';
            button.style.transform = '';
            button.style.transition = '';
        //   }
        }
      } else {
        button.classList.remove('keyboard-active');

        if (isIOS) {
          // iOS：恢复
          button.style.transform = '';
          button.style.transition = '';
          button.style.zIndex = '';
        } else {
          // 非 iOS：恢复页面和按钮状态
          this.pushPageContent(button, false);
          button.style.position = 'fixed';
          button.style.bottom = '24px';
          button.style.zIndex = '1000';
          button.style.transform = '';
          button.style.transition = '';
        }
      }
    });
  }

  // 判断是否应该推整个页面而不是只弹起按钮
  shouldPushPageInsteadOfButton(button) {
    // 检查是否禁用了页面推送功能
    const pushPageEnabled = button.getAttribute('data-push-page') !== 'false';
    if (!pushPageEnabled) {
      return false;
    }
    
    try {
      // 获取页面主要内容区域
      const mainContent = this.findMainContent(button);
      if (!mainContent) {
        return false; // 找不到主内容，只弹起按钮
      }
      
      // 计算内容区域的位置
      const contentRect = mainContent.getBoundingClientRect();
      const contentBottom = contentRect.bottom;
      
      // 获取视口信息
      const viewport = window.visualViewport;
      if (!viewport) {
        return false; // 降级处理
      }
      
      // 计算键盘遮挡后的可用高度
      const availableHeight = viewport.height + viewport.offsetTop;
      const keyboardHeight = window.innerHeight - availableHeight;
      
      // 如果内容底部在键盘上方，不需要推页面
      if (contentBottom <= availableHeight) {
        return false;
      }
      
      // 计算按钮弹起后是否会遮挡内容
      const buttonHeight = button.offsetHeight;
      const buttonTop = availableHeight - buttonHeight - 12; // 12px 间距
      
      // 如果按钮弹起后会遮挡内容，则推整个页面
      if (buttonTop < contentBottom) {
        return true;
      }
      
      return false;
    } catch (error) {
      console.warn('Content overlap detection failed:', error);
      return false; // 检测失败时只弹起按钮
    }
  }

  // 推整个页面内容
  pushPageContent(button, shouldPush) {
    try {
      // 查找页面主容器
      const pageContainer = this.findPageContainer(button);
      if (!pageContainer) {
        console.warn('No page container found for button:', button);
        return;
      }
      
      if (shouldPush) {
        // 计算需要推上去的距离
        const viewport = window.visualViewport;
        if (!viewport) {
          console.warn('No visualViewport support for page push');
          return;
        }
        
        const bottomObstruction = Math.max(0, (window.innerHeight - (viewport.height + viewport.offsetTop)));
        const buttonHeight = button.offsetHeight;
        const safeGap = Number(button.getAttribute('data-safe-gap') || 12); // 可配置的安全间距
        
        // 推上去的距离 = 键盘遮挡高度 - 按钮高度 - 安全间距
        const pushDistance = Math.max(0, bottomObstruction - buttonHeight - safeGap);
        
        if (pushDistance > 0) {
          // 添加过渡动画
          pageContainer.style.transition = 'transform 0.3s ease-out';
          pageContainer.style.transform = `translateY(-${pushDistance}px)`;
          
          // 记录推上去的距离，用于恢复
          pageContainer.dataset.pushDistance = pushDistance;
        }
      } else {
        // 恢复页面位置
        const pushDistance = pageContainer.dataset.pushDistance;
        if (pushDistance) {
          pageContainer.style.transition = 'transform 0.3s ease-out';
          pageContainer.style.transform = '';
          delete pageContainer.dataset.pushDistance;
        }
      }
    } catch (error) {
      console.warn('Failed to push page content:', error);
    }
  }

  // 查找页面主容器
  findPageContainer(button) {
    // 检查是否有自定义容器选择器
    const customContainer = button.getAttribute('data-page-container');
    if (customContainer) {
      const element = document.querySelector(customContainer);
      if (element) return element;
    }
    
    // 优先级1：从按钮向上查找布局容器（最准确）
    const componentGridLayout = button.closest('.component-grid-layout');
    if (componentGridLayout) {
      return componentGridLayout;
    }
    
    const responsiveLayout = button.closest('.responsive-layout');
    if (responsiveLayout) {
      return responsiveLayout;
    }
    
    const rootMobileLayout = button.closest('.root-mobile-layout');
    if (rootMobileLayout) {
      return rootMobileLayout;
    }
    
    const rootDesktopLayout = button.closest('.root-desktop-layout');
    if (rootDesktopLayout) {
      return rootDesktopLayout;
    }
    
    // 优先级2：全局查找布局容器（如果按钮不在布局内，查找页面中的布局）
    const rootMobileLayoutGlobal = document.querySelector('.root-mobile-layout');
    if (rootMobileLayoutGlobal) {
      return rootMobileLayoutGlobal;
    }
    
    const componentGridLayoutGlobal = document.querySelector('.component-grid-layout');
    if (componentGridLayoutGlobal) {
      return componentGridLayoutGlobal;
    }
    
    const responsiveLayoutGlobal = document.querySelector('.responsive-layout');
    if (responsiveLayoutGlobal) {
      return responsiveLayoutGlobal;
    }
    
    const rootDesktopLayoutGlobal = document.querySelector('.root-desktop-layout');
    if (rootDesktopLayoutGlobal) {
      return rootDesktopLayoutGlobal;
    }
    
    // 优先级3：查找主容器
    const mainContainer = document.querySelector('main, .main, .page-container, .content-wrapper');
    if (mainContainer) return mainContainer;
    
    // 优先级4：查找表单容器
    const form = button.closest('form');
    if (form) {
      const formContainer = form.closest('.form-container, .form-wrapper, .input-container');
      if (formContainer) return formContainer;
      return form;
    }
    
    // 优先级5：查找包含按钮的最近父容器
    const buttonContainer = button.closest('.button-container, .cta-container, .action-container');
    if (buttonContainer) return buttonContainer;
    
    // 优先级6：查找 body 的直接子容器
    const bodyChildren = Array.from(document.body.children);
    const contentChild = bodyChildren.find(child => 
      child.tagName !== 'SCRIPT' && 
      child.tagName !== 'STYLE' && 
      child.tagName !== 'NOSCRIPT' &&
      !child.classList.contains('component-cta-button')
    );
    
    if (contentChild) return contentChild;
    
    // 降级：使用 body
    return document.body;
  }

  // 查找页面主要内容区域
  findMainContent(button) {
    // 优先级1：查找表单元素
    const form = button.closest('form');
    if (form) return form;
    
    // 优先级2：查找主内容区域
    const main = document.querySelector('main');
    if (main) return main;
    
    // 优先级3：查找文章内容
    const article = document.querySelector('article');
    if (article) return article;
    
    // 优先级4：查找特定类名
    const contentSelectors = [
      '.content',
      '.main-content',
      '.page-content',
      '.form-content',
      '.input-group'
    ];
    
    for (const selector of contentSelectors) {
      const element = document.querySelector(selector);
      if (element) return element;
    }
    
    // 优先级5：查找包含输入框的最近父容器
    const inputContainer = button.closest('.input-group, .form-group, .field-group');
    if (inputContainer) return inputContainer;
    
    return null;
  }
}

// 导出类供外部使用（必须在初始化函数之前定义）
window.CTAButtonComponent = CTAButtonComponent;

// 初始化函数（避免重复初始化）
function initializeCTAButtonComponent() {
  // 确保 window.CTAButtonComponent 已定义
  if (!window.CTAButtonComponent) {
    window.CTAButtonComponent = CTAButtonComponent;
  }
  
  // 检查是否已有实例，避免重复初始化
  if (!window.CTAButtonComponent.instance) {
    const instance = new CTAButtonComponent();
    // 保存实例到全局，供测试和调试使用
    window.CTAButtonComponent.instance = instance;
  }
}

// 页面加载完成后初始化
if (document.readyState === 'loading') {
  // DOM 仍在加载，等待 DOMContentLoaded
  document.addEventListener('DOMContentLoaded', initializeCTAButtonComponent);
} else {
  // DOM 已经加载完成，立即初始化（支持动态加载的脚本）
  initializeCTAButtonComponent();
}

// 创建全局实例供外部使用
window.CTAButton = {
  setSoftDisabled: (button, isDisabled, newText = null) => {
    const label = button.querySelector('.component-cta-button__label');
    button.disabled = isDisabled;
    
    // 不添加 --disabled 样式类，保持原有样式
    if (!isDisabled) {
      button.classList.add('component-cta-button--enabled');
      button.classList.remove('component-cta-button--pressed');
      // 确保移除 disabled 类，以防之前存在
      button.classList.remove('component-cta-button--disabled');
    }
    
    if (newText && label) {
      label.textContent = newText;
    }
  },
  
  setEnabled: (button, isEnabled, newText = null) => {
    const label = button.querySelector('.component-cta-button__label');
    button.disabled = !isEnabled;
    
    // 保持样式稳定：仅在启用时确保有 enabled 类，禁用时不添加 --disabled 类
    if (isEnabled) {
      button.classList.add('component-cta-button--enabled');
      button.classList.remove('component-cta-button--pressed');
    }
    
    if (newText && label) {
      label.textContent = newText;
    }
  },

  // 测试页面推送功能
  testPagePush: (button) => {
    if (!button) {
      console.error('No button provided for testing');
      return;
    }
    
    // 模拟键盘弹出状态
    const testViewport = {
      height: window.innerHeight - 300, // 模拟键盘遮挡300px
      offsetTop: 0
    };
    
    // 临时替换 visualViewport 进行测试
    const originalViewport = window.visualViewport;
    Object.defineProperty(window, 'visualViewport', {
      value: testViewport,
      writable: true
    });
    
    try {
      // 测试检测逻辑
      const component = window.CTAButtonComponent?.instance || new CTAButtonComponent();
      const shouldPush = component.shouldPushPageInsteadOfButton(button);
      
      if (shouldPush) {
        // 测试页面推送
        component.pushPageContent(button, true);
      }
    } finally {
      // 恢复原始 viewport
      Object.defineProperty(window, 'visualViewport', {
        value: originalViewport,
        writable: true
      });
    }
  },

  // 手动触发页面推送（用于测试）
  manualPagePush: (button, distance = 100) => {
    if (!button) {
      console.error('No button provided for manual push');
      return;
    }
    
    const component = window.CTAButtonComponent?.instance || new CTAButtonComponent();
    const pageContainer = component.findPageContainer(button);
    
    if (pageContainer) {
      pageContainer.style.transition = 'transform 0.3s ease-out';
      pageContainer.style.transform = `translateY(-${distance}px)`;
      pageContainer.dataset.pushDistance = distance;
    } else {
      console.error('No page container found for manual push');
    }
  },

  // 恢复页面位置
  restorePagePosition: (button) => {
    if (!button) {
      console.error('No button provided for restore');
      return;
    }
    
    const component = window.CTAButtonComponent?.instance || new CTAButtonComponent();
    component.pushPageContent(button, false);
  },

  // 配置全局防抖
  setGlobalDebounce: (options = {}) => {
    const component = window.CTAButtonComponent?.instance || new CTAButtonComponent();
    component.setGlobalDebounce(options);
  }
}; 

/**
 * CTA 按钮智能页面推送功能说明
 * 
 * 新增功能：当按钮弹起会遮挡内容时，自动将整个页面内容向上推
 * 
 * 配置选项：
 * 
 * 1. data-push-page: 是否启用页面推送功能
 *    - "true": 启用（默认）
 *    - "false": 禁用，只弹起按钮
 * 
 * 2. data-safe-gap: 安全间距（像素）
 *    - 默认值: 20px
 *    - 页面内容与按钮之间的安全距离
 * 
 * 3. data-page-container: 指定页面容器选择器
 *    - 默认: 自动检测
 *    - 自定义: 指定要推送的容器元素
 * 
 * 使用示例：
 * 
 * <!-- 启用页面推送（推荐） -->
 * <button class="component-cta-button" 
 *         data-stick-to-bottom="true"
 *         data-push-page="true"
 *         data-safe-gap="25">
 *   智能提交
 * </button>
 * 
 * <!-- 禁用页面推送，只弹起按钮 -->
 * <button class="component-cta-button" 
 *         data-stick-to-bottom="true"
 *         data-push-page="false">
 *   只弹起按钮
 * </button>
 * 
 * <!-- 指定页面容器 -->
 * <button class="component-cta-button" 
 *         data-stick-to-bottom="true"
 *         data-page-container=".my-form-container">
 *   指定容器推送
 * </button>
 * 
 * 工作原理：
 * 1. 检测按钮弹起后是否会遮挡页面内容
 * 2. 如果会遮挡，计算需要推送的距离
 * 3. 使用 transform: translateY() 将整个页面内容向上推
 * 4. 按钮保持贴底，确保可见性
 * 5. 键盘收起时自动恢复页面位置
 * 
 * 优势：
 * - 避免按钮遮挡重要内容
 * - 保持页面整体布局
 * - 平滑的过渡动画
 * - 自动检测和恢复
 */ 
